<template>
  <div
    id="yourpunks"
    class="content resizable draggable-window hidden"
  >
    <div class="control-box close-box">
      <a class="control-box-inner" />
    </div>
    <div class="control-box zoom-box">
      <div class="control-box-inner">
        <div class="zoom-box-inner" />
      </div>
    </div>
    <div class="control-box window-collapse">
      <div class="control-box-inner">
        <div class="window-collapse-inner" />
      </div>
    </div>
    <h1 class="titlebar">
      <span>{{ selectedPunk ? selectedPunk.name : 'Your RPUNKS' }}</span>
    </h1>
    <div class="inner">
      <div
        v-if="selectedPunk"
        class="punk-detail"
      >
        <button
          class="command_button back-button"
          @click="selectedId = undefined"
        >
          &lsaquo; All Punks
        </button>
        <PunkDetail
          :punk="selectedPunk"
          @suit-up="$emit('suit-up', $event)"
        />
      </div>

      <template v-else>
        <p
          v-if="loading && !punks.length"
          class="punks-status"
        >
          Scanning the wasteland for your punks...
        </p>
        <p
          v-else-if="error"
          class="punks-status"
        >
          {{ error }}
        </p>
        <p
          v-else-if="!punks.length"
          class="punks-status"
        >
          This wallet doesn't hold any Radioactive Punks yet.<br><br>
          <a
            href="https://opensea.io/collection/radioactive-punks"
            target="_blank"
            rel="noopener"
          >Find one on OpenSea</a>
        </p>
        <template v-else>
          <p class="punks-count">
            {{ punks.length }} {{ punks.length === 1 ? 'punk' : 'punks' }} in this wallet
          </p>
          <div class="punk-grid">
            <button
              v-for="punk in punks"
              :key="punk.id"
              class="punk-tile"
              :title="punk.name"
              @click="selectedId = punk.id"
            >
              <div class="punk-tile-image">
                <img
                  v-if="punk.image"
                  :src="punk.image"
                  alt=""
                >
                <span
                  v-else-if="punk.error"
                  class="punk-tile-placeholder"
                >?</span>
                <LoadingSpinner v-else />
              </div>
              <span class="punk-tile-label">#{{ punk.id }}</span>
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import RADIOACTIVEPUNKS from '@/assets/RADIOACTIVEPUNKS.json'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PunkDetail from '@/components/PunkDetail.vue'
import { RPUNKS_ADDRESS, fetchPunkMetadata, read, readMany } from '@/web3'

const rpunks = { address: RPUNKS_ADDRESS, abi: RADIOACTIVEPUNKS.abi }

// each punk's metadata is a couple of contract calls, so fetch a handful at a time
const METADATA_BATCH_SIZE = 6

export default {
  name: 'YourPunksWindow',
  components: {
    LoadingSpinner,
    PunkDetail
  },
  props: {
    accountAddress: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      punks: [],
      selectedId: undefined,
      loading: false,
      error: '',
      loadedFor: ''
    }
  },
  computed: {
    selectedPunk () {
      return this.punks.find(punk => punk.id === this.selectedId)
    }
  },
  watch: {
    accountAddress () {
      this.reset()
    }
  },
  methods: {
    reset () {
      this.punks = []
      this.selectedId = undefined
      this.error = ''
      this.loadedFor = ''
    },

    // fetches the wallet's punks once per address; the metadata streams into the grid as it arrives
    async load () {
      const address = this.accountAddress
      if (!address || this.loading || this.loadedFor === address) { return }

      this.reset()
      this.loading = true
      try {
        const balance = Number(await read({ ...rpunks, functionName: 'balanceOf', args: [address] }))
        const tokenIds = await readMany(
          Array.from({ length: balance }, (_, i) => ({
            ...rpunks,
            functionName: 'tokenOfOwnerByIndex',
            args: [address, BigInt(i)]
          }))
        )
        if (address !== this.accountAddress) { return }

        this.punks = tokenIds
          .map(Number)
          .sort((a, b) => a - b)
          .map(id => ({ id, name: `Radioactive Punk #${id}`, attributes: undefined, image: undefined, error: false }))
        this.loadedFor = address

        for (let i = 0; i < this.punks.length; i += METADATA_BATCH_SIZE) {
          await Promise.all(this.punks.slice(i, i + METADATA_BATCH_SIZE).map(this.loadMetadata))
          if (address !== this.accountAddress) { return }
        }
      } catch (e) {
        console.warn(e)
        this.error = 'Couldn\'t load your punks. Close this window and try again.'
        this.loadedFor = ''
      } finally {
        this.loading = false
      }
    },

    async loadMetadata (punk) {
      try {
        const data = await fetchPunkMetadata(punk.id)
        Object.assign(punk, {
          name: data.name || punk.name,
          attributes: data.attributes,
          image: data.image
        })
      } catch (e) {
        console.warn(`Could not load metadata for punk #${punk.id}`, e)
        punk.error = true
      }
    }
  }
}
</script>

<style lang="scss">
#yourpunks {
  left: 60px;
  top: 50px;

  .inner {
    width: 460px;
    height: 400px;

    @media (max-width: 800px) {
      width: 300px;
    }
  }

  .punk-tile-image img {
    display: block;
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
  }
}

.punks-status,
.punks-count {
  text-align: center;
  margin: 1em 0;
}

.punk-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 10px;
}

.punk-tile {
  background: #fff;
  border: 1px solid #111;
  padding: 4px;
  cursor: pointer;
  font: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover,
  &:focus {
    outline: 2px solid #3eae2e;
    outline-offset: -1px;
  }
}

.punk-tile-image {
  width: 100%;
  aspect-ratio: 1;
  background: #e9f7de;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.punk-tile-placeholder {
  font-size: 2rem;
}

.punk-tile-label {
  margin-top: 4px;
  font-size: 0.9rem;
}
</style>
